import React, {useEffect, useRef, useState} from 'react';

import {FlowEnum} from '../../constants';
import {useQueryParamsSync} from '../../hooks/useQueryParamsSync';
import {DistrictSelection} from '../DistrictSelection';

import {ProfessionalSelection} from '../ProfessionalSelection';
import {ReviewBooking} from '../ReviewBooking';
import {SpecialtySelection} from '../SpecialtySelection';
import {UnitSelection} from '../UnitSelection';
import {BookingStepsProps} from './types';
import {Location} from 'presentation/modules/booking/shared/ui/pages/Location';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {HealthUnits} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthUnits';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {TelehealthReviewBookingNew} from 'presentation/modules/booking/telehealth/ui/pages/TelehealthBooking/steps/TelehealthReviewBookingNew';
import {HealthReviewBookingNew} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthReviewBookingNew';
import {ExamReviewBookingNew} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamReviewBookingNew';
import {OdontoReviewBookingNew} from 'presentation/modules/booking/odonto/ui/pages/OdontoBooking/steps/OdontoReviewBookingNew';

export function BookingSteps({
	activeStep,
	flowType,
	handleBack,
	handleNext,
	stepLabels,
}: BookingStepsProps) {
	const stepsVisited = useRef(new Set<number>());
	const [isFirstVisit, setIsFirstVisit] = useState<boolean>(true);

	const isExamFlow = flowType === FlowEnum.EXAM;
	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const isTelehealthFlow = flowType === FlowEnum.TELEHEALTH;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;

	const bookingData = useBookingStore(state => state.bookingData);
	const isUsingGeolocation =
		!bookingData.isManualLocation && (isExamFlow || isHealthFlow || isOdontoFlow);

	const {
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
	} = getStepPosition({flowType, isManualLocation: bookingData.isManualLocation});

	useEffect(() => {
		const isFirstVisitValue = !stepsVisited.current.has(activeStep);
		setIsFirstVisit(isFirstVisitValue);
		stepsVisited.current.add(activeStep);
	}, [activeStep]);

	useQueryParamsSync({isFirstVisit});

	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);

	const getNewReviewStep = () => {
		if (isTelehealthFlow) {
			return <TelehealthReviewBookingNew handlePrevStep={handleBack} />;
		}
		if (isHealthFlow) {
			return <HealthReviewBookingNew handlePrevStep={handleBack} />;
		}
		if (isExamFlow) {
			return <ExamReviewBookingNew handlePrevStep={handleBack} />;
		}
		return <OdontoReviewBookingNew handlePrevStep={handleBack} />;
	};

	const reviewStepContent = isNewResumePageEnabled ? (
		getNewReviewStep()
	) : (
		<ReviewBooking
			flowType={flowType}
			handlePrevStep={handleBack}
			activeStep={activeStep}
			stepLabels={stepLabels}
		/>
	);

	if (isTelehealthFlow) {
		const STEP_CONTENT: Record<number, React.JSX.Element | null> = {
			[SPECIALITY_OR_EXAM_STEP]: (
				<SpecialtySelection
					flowType={flowType}
					handleNextStep={handleNext}
					handlePrevStep={handleBack}
				/>
			),
			[PROFESSIONAL_STEP]: (
				<ProfessionalSelection
					flowType={flowType}
					handleNextStep={handleNext}
					handlePrevStep={handleBack}
				/>
			),
			[REVIEW_STEP]: reviewStepContent,
			3: null,
			4: null,
		};

		return STEP_CONTENT[activeStep];
	}

	if (isUsingGeolocation) {
		const STEP_CONTENT: Record<number, React.JSX.Element | null> = {
			[LOCATION_STEP]: <Location flowType={flowType} />,
			[SPECIALITY_OR_EXAM_STEP]: (
				<SpecialtySelection
					flowType={flowType}
					handleNextStep={handleNext}
					handlePrevStep={handleBack}
				/>
			),
			[UNIT_STEP]: isHealthFlow ? (
				<HealthUnits handleNextStep={handleNext} />
			) : (
				<UnitSelection
					flowType={flowType}
					handleNextStep={handleNext}
					handlePrevStep={handleBack}
				/>
			),
			[PROFESSIONAL_STEP]: (
				<ProfessionalSelection
					flowType={flowType}
					handleNextStep={handleNext}
					handlePrevStep={handleBack}
				/>
			),
			[REVIEW_STEP]: reviewStepContent,
		};

		return STEP_CONTENT[activeStep];
	}

	const STEP_CONTENT: Record<number, React.JSX.Element | null> = {
		[LOCATION_STEP]: <Location flowType={flowType} />,
		[SPECIALITY_OR_EXAM_STEP]: (
			<SpecialtySelection
				flowType={flowType}
				handleNextStep={handleNext}
				handlePrevStep={handleBack}
			/>
		),
		[DISTRICT_STEP]: (
			<DistrictSelection
				flowType={flowType}
				handleNextStep={handleNext}
				handlePrevStep={handleBack}
			/>
		),
		[UNIT_STEP]: isHealthFlow ? (
			<HealthUnits handleNextStep={handleNext} />
		) : (
			<UnitSelection
				flowType={flowType}
				handleNextStep={handleNext}
				handlePrevStep={handleBack}
			/>
		),
		[PROFESSIONAL_STEP]: (
			<ProfessionalSelection
				flowType={flowType}
				handleNextStep={handleNext}
				handlePrevStep={handleBack}
			/>
		),
		[REVIEW_STEP]: reviewStepContent,
	};

	return STEP_CONTENT[activeStep];
}
